import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { LuMedal } from 'react-icons/lu';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

function Section({ children, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} style={style}>
      {children}
    </motion.div>
  );
}

const milestones = [
  { year: '2015', title: 'The Vision', desc: 'After living across Africa, Europe, and the Middle East, Nimmy & Dr. Raphi Paul dream of creating a home where the world can come to rest.' },
  { year: '2018', title: 'Doors Open', desc: 'Villa Tesori welcomes its first guests in the misty hills of Vellarimala, Wayanad. The name "Tesori" — Italian for treasure — was born.' },
  { year: '2019', title: 'Diamond Class Certified', desc: 'Receives Diamond Class Certification from the Department of Tourism, Govt. of Kerala — the highest honour for a homestay.' },
  { year: '2024', title: '500+ Guests', desc: 'Guests from three continents have experienced the magic of Villa Tesori. 5-star rated on TripAdvisor and Kerala Tourism.' },
];

export default function About() {
  return (
    <PageWrapper>
      <Helmet>
        <title>Our Story – Villa Tesori | Best Diamond Class Homestay in Wayanad</title>
        <meta name="description" content="Meet your hosts Nimmy and Dr. Raphi Paul. Learn the story behind Villa Tesori, Wayanad's top-rated Diamond Class certified homestay with a global soul and Kerala heart." />
        <meta name="keywords" content="Villa Tesori story, Nimmy and Raphi Paul, Wayanad homestay hosts, Kerala Diamond Class certification, best homestay experience Wayanad, luxury homestay Kerala" />
        <meta property="og:title" content="Our Story – Villa Tesori Homestay" />
        <meta property="og:description" content="From living across continents to building a dream homestay in the peaks of Wayanad. Discover our story." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/about" />
        <link rel="canonical" href="https://villatesori.com/about" />
      </Helmet>

      <PageHero
        scriptText="Our Story"
        title="About Villa Tesori"
        subtitle="A treasure built with love, laughter, and a lifelong dream"
        bgImage="/img2.jpeg"
      />

      {/* Story Section */}
      <section className="section">
        <div className="container">
          <div className="about-story-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <Section>
              <div className="section-label">The Name</div>
              <h2 className="section-title">What is a Tesori?</h2>
              <div className="divider" />
              <p style={{ lineHeight: 1.9, marginBottom: 16 }}>
                In Italian, <em>"Tesori"</em> means <strong>treasure</strong>. When Nimmy and Dr. Raphi Paul searched for a name worthy of this hilltop sanctuary, they found it in the language of beauty and romance.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 16 }}>
                Having worked and lived across Africa, Europe, and the Middle East, they brought a world of experiences back to their home state of Kerala, weaving multicultural warmth into every corner of this villa.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.9 }}>
                Perched atop a small hill surrounded by tea plantations, overlooking the majestic Chembra Peak, Villa Tesori is a 360-degree view of what God's Own Country truly means.
              </p>
            </Section>
            <Section>
              <div className="about-imgs-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="img-overlay" style={{ height: 280, borderRadius: 2 }}>
                  <img src="/img5.jpeg" alt="Villa Life" />
                </div>
                <div className="img-overlay" style={{ height: 280, borderRadius: 2, marginTop: 24 }}>
                  <img src="/img6.jpeg" alt="Villa Lifestyle" />
                </div>
              </div>
            </Section>
          </div>
        </div>
      </section>

      {/* Hosts */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <Section style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>Meet Your Hosts</div>
            <h2 className="section-title">Nimmy & Dr. Raphi Paul</h2>
          </Section>
          <div className="about-hosts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 60, alignItems: 'center' }}>
            <Section>
              <div className="img-overlay about-hosts-img" style={{ height: 520, borderRadius: 2 }}>
                <img src="/hostrealimg.jpeg" alt="Nimmy & Dr. Raphi Paul" />
              </div>
            </Section>
            <Section>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontStyle: 'italic', color: 'var(--forest)', lineHeight: 1.6, marginBottom: 24 }}>
                "We want every guest to feel like family — fed with love, treated like royalty, and sent home with memories that last a lifetime."
              </p>
              <div style={{ width: 40, height: 1, background: 'var(--gold)', marginBottom: 24 }} />
              <p style={{ lineHeight: 1.9, marginBottom: 16 }}>
                Nimmy is a full-time host who brings warmth, creativity, and exceptional home cooking to every stay. Dr. Raphi Paul, a part-time host, adds intellectual depth and an adventurer's spirit to the experience.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 24 }}>
                Together they have hosted guests from India, Europe, Africa, the Americas, and beyond — bringing the world together under one rooftop in the Western Ghats.
              </p>
              <div className="about-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
                {[['3', 'Continents Lived'],['10+', 'Years Hosting'],['5.0', 'TripAdvisor Rating']].map(([n, l]) => (
                  <div key={l} style={{ background: 'var(--white)', padding: '20px 16px', textAlign: 'center', borderRadius: 2 }}>
                    <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: 'var(--gold)' }}>{n}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{l}</div>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn btn-primary">Plan Your Visit</Link>
            </Section>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section" style={{ background: 'var(--forest)' }}>
        <div className="container-sm">
          <Section style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>Our Journey</div>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 5vw, 3rem)', color: 'var(--mist)' }}>Milestones</h2>
          </Section>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
            {milestones.map((m, i) => (
              <Section key={m.year}>
                <div style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 4,
                  padding: '28px 32px',
                  borderLeft: '3px solid var(--gold)',
                }}>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.2rem', color: 'var(--gold)', lineHeight: 1, marginBottom: 8 }}>{m.year}</div>
                  <h3 style={{ color: 'var(--mist)', fontSize: '1.2rem', margin: '0 0 10px' }}>{m.title}</h3>
                  <p style={{ color: 'rgba(200,217,192,0.7)', fontSize: '0.9rem', lineHeight: 1.8, margin: 0 }}>{m.desc}</p>
                </div>
              </Section>
            ))}
          </div>
        </div>
      </section>

      {/* Certification */}
      <section className="section-sm" style={{ background: 'var(--parchment)', textAlign: 'center' }}>
        <div className="container-sm">
          <Section>
            <div className="about-cert-box" style={{ display: 'inline-flex', alignItems: 'center', gap: 24, background: 'var(--white)', padding: '24px 44px', borderRadius: 2, border: '1px solid rgba(201,168,76,0.3)', maxWidth: '100%' }}>
              <span style={{ fontSize: '2.4rem', color: 'var(--gold)', flexShrink: 0 }}><LuMedal /></span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', color: 'var(--forest)' }}>Diamond Class Certified Homestay</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Department of Tourism, Govt. of Kerala · RT Mission</div>
              </div>
            </div>
          </Section>
        </div>
      </section>
    </PageWrapper>
  );
}
